import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SentenceOfTheDay {
  id: string;
  sentence: string;
  usage_context: string;
  time_context?: string;
  example_situation?: string;
  category: string;
  difficulty_level: 'beginner' | 'intermediate' | 'advanced';
  seo_title?: string;
  meta_description?: string;
  keywords?: string[];
}

export interface SentenceOfTheDaySelection {
  id: string;
  sentence_id: string;
  sentences_of_the_day: SentenceOfTheDay;
}

export const useSentencesData = () => {
  const [sentences, setSentences] = useState<SentenceOfTheDay[]>([]);
  const [sentenceOfTheDay, setSentenceOfTheDay] = useState<SentenceOfTheDaySelection | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSentences = async () => {
    try {
      const { data, error } = await supabase
        .from('sentences_of_the_day')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSentences(data || []);
    } catch (error) {
      console.error('Error fetching sentences:', error);
    }
  };

  const fetchSentenceOfTheDay = async () => {
    try {
      const { data, error } = await supabase
        .from('sentence_of_the_day')
        .select(`
          id,
          sentence_id,
          sentences_of_the_day (
            id,
            sentence,
            usage_context,
            time_context,
            example_situation,
            category,
            difficulty_level,
            seo_title,
            meta_description,
            keywords
          )
        `)
        .eq('is_active', true)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      
      // Handle the case where sentences_of_the_day is returned as an array
      if (data && data.sentences_of_the_day) {
        const sentenceData = Array.isArray(data.sentences_of_the_day) 
          ? data.sentences_of_the_day[0] 
          : data.sentences_of_the_day;
        
        if (sentenceData) {
          setSentenceOfTheDay({
            id: data.id,
            sentence_id: data.sentence_id,
            sentences_of_the_day: sentenceData
          });
        }
      }
    } catch (error) {
      console.error('Error fetching sentence of the day:', error);
    }
  };

  const refetch = async () => {
    setLoading(true);
    await Promise.all([fetchSentences(), fetchSentenceOfTheDay()]);
    setLoading(false);
  };

  useEffect(() => {
    refetch();
  }, []);

  return {
    sentences,
    sentenceOfTheDay,
    loading,
    refetch,
  };
};